<template>
  <v-layout align-center>
    <v-flex xs12 sm10 md10 lg8>
      <div>
        <v-layout wrap>
          <v-flex xs12>
            <div v-if="$apollo.loading">
              <spinner />
            </div>
            <div v-else>
              <div class="title-screen">
                <span>{{ adminText('workflowsAll.event_info_8f142f7', 'Event info') }}</span>
              </div>
              <event-info-form
                :vs="$v.form.generalInfo"
                :initial-values="form.generalInfo"
                :categories="categories"
                :show-buttons="false"
              />
              <counter-field
                v-if="extraModules.categoriesAndZones"
                v-model="form.generalInfo.numberOfZones"
                :min-value="1"
                :error-messages="{ between: adminText('workflowsAll.this_field_should_be_between_1_and_10_128340b', 'This field should be between 1 and 10') }"
                :label="adminText('workflowsAll.number_of_zones_b7e410b', 'Number of zones')"
                :validator="$v.form.generalInfo.numberOfZones"
              />
              <div v-if="isEventPublished && allowPublishEventsToLanding" class="update-button">
                <v-tooltip top color="rgba(51, 51, 51, 0.80)" max-width="10rem">
                  {{ adminText('workflowsAll.save_your_progress_without_publishing_the_cha_0cd1bdd', 'Save your progress without publishing the changes.') }}
                  <main-button
                    slot="activator"
                    :text="adminText('workflowsAll.save_draft_4f25637', 'Save draft')"
                    outline
                    :disabled="!isFormValid"
                    @click="submitForm('submit')"
                  />
                </v-tooltip>
                <v-tooltip top color="rgba(51, 51, 51, 0.80)" max-width="10rem">
                  {{ adminText('workflowsAll.apply_and_make_the_changes_visible_to_users_031e1b9', 'Apply and make the changes visible to users.') }}
                  <main-button
                    slot="activator"
                    :text="adminText('workflowsAll.publish_changes_45d2840', 'Publish changes')"
                    :disabled="!isFormValid"
                    @click="submitForm('submitAndPublish')"
                  />
                </v-tooltip>
              </div>
              <div v-else class="update-button">
                <main-button
                  :text="adminText('workflowsAll.update_fb91e24', 'Update')"
                  depressed
                  :disabled="!isFormValid"
                  @click="submitForm('submit')"
                />
              </div>
            </div>
          </v-flex>
        </v-layout>
      </div>
    </v-flex>
    <simple-snackbar :open.sync="snackbar.open" :message-prop="workflowLabel(snackbar.messageProp)" :color="snackbar.color" />
  </v-layout>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { required, between, minValue, maxLength, requiredIf } from 'vuelidate/lib/validators'
import { date, minDate, maxDate, httpUrl } from './../../validators'

import { GET_EVENT_SETTINGS_AND_LOGGED_USER, UPDATE_EVENT } from '../../graphql/event.graphql'
import { GET_ALL_EVENT_CATEGORIES } from '../../graphql/eventCategory.graphql'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar'
import Spinner from '../shared/spinner'
import { submitGuard } from './../../utils'
import EventInfoForm from './event-info-form.vue'
import { EventStatus } from '@/types'

export default {
  mixins: [WorkflowDisplay],
  name: 'event-info-container',
  components: {
    SimpleSnackbar,
    Spinner,
    EventInfoForm,
  },
  props: {
    eventId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      snackbar: { open: false, color: 'primary', messageProp: '' },
      isSubcategoryRequiredField: false,
      extraModules: {},
      categories: [],
      form: {},
      isEventPublished: false,
      allowPublishEventsToLanding: false,
    }
  },
  validations() {
    return {
      form: {
        generalInfo: {
          title: { required, maxLength: maxLength(120) },
          eventCategoryId: { required },
          interests: {
            required: requiredIf(() => this.isSubcategoryRequiredField),
          },
          from: {
            required,
            date,
            maxDate: maxDate(() => new Date(this.form.generalInfo.to)),
          },
          to: {
            required,
            date,
            minDate: minDate(() => new Date(this.form.generalInfo.from)),
          },
          location: {
            venue: { required },
            address1: { required },
          },
          highlightedData: {
            ageRestriction: {
              value: {
                required: requiredIf(
                  () => this.form.generalInfo.highlightedData.ageRestriction.enabled,
                ),
                minValue: minValue(0),
              },
            },
            freeEntranceWithAgeLimit: {
              value: {
                required: requiredIf(
                  () => this.form.generalInfo.highlightedData.freeEntranceWithAgeLimit.enabled,
                ),
                minValue: minValue(0),
              },
            },
          },
          faqs: {
            $each: {
              answer: { required },
              question: { required },
            },
          },
          metadata: {
            webLink: {
              httpUrl,
            },
            facebookLink: {
              httpUrl,
            },
          },
        },
        numberOfZones: {
          required: requiredIf(({ modules }) => modules && modules.categoriesAndZones),
          between: between(1, 10),
        },
      },
    }
  },
  computed: {
    isFormValid() {
      return !this.$v.$error && !this.$v.$invalid
    },
  },
  watch: {
    'form.generalInfo.eventCategoryId': function () {
      const category = this.categories.find(c => c.id === this.form.generalInfo.eventCategoryId)
      if (!category) {
        return (this.isSubcategoryRequiredField = false)
      }

      return (this.isSubcategoryRequiredField = !!category.subCategories.length)
    },
  },
  methods: {
    async submitForm(action) {
      await submitGuard(this, async () => {
        const { metadata, location, interests, ...rest } = this.form.generalInfo
        const locationForSubmit = { ...location }
        delete locationForSubmit.__typename
        const metadataForSubmit = { ...(metadata || {}) }
        delete metadataForSubmit.skidata

        await this.$apollo.mutate({
          mutation: UPDATE_EVENT,
          variables: {
            id: this.eventId,
            event: {
              ...rest,
              metadata: metadataForSubmit,
              interests,
              eventCategoryId: this.form.generalInfo.eventCategoryId,
            },
            location: locationForSubmit,
            publishChanges: action === 'submitAndPublish',
          },
        })
        this.snackbar = {
          open: true,
          messageProp: action === 'submitAndPublish' ? 'workflowsAll.event_details_saved_and_will_be_live_in_20_mi_fafed0f' : 'workflowsAll.event_details_saved_7e804fe',
          color: 'primary',
        }
      })
    },
  },
  apollo: {
    getEvent: {
      query: GET_EVENT_SETTINGS_AND_LOGGED_USER,
      variables() {
        return {
          id: this.eventId,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (!loading && data) {
          const {
            title,
            cover,
            description,
            to,
            from,
            location,
            numberOfZones,
            extraModules,
            interests,
            eventCategory,
            organizerMessage,
            highlightedData,
            faqs,
            metadata,
            status,
          } = data.getEvent
          const { flags } = data.me

          this.allowPublishEventsToLanding = flags && flags.allowPublishEventsToLanding

          this.isEventPublished = status === EventStatus.PUBLISHED
          this.extraModules = extraModules
          this.form = {
            generalInfo: {
              title,
              from,
              to,
              location,
              numberOfZones,
              eventCategoryId: eventCategory.id,
              description,
              cover,
              organizerMessage,
              interests: interests.map(i => i.id),
              highlightedData,
              faqs,
              metadata: metadata ? metadata : { webLink: '', facebookLink: '' },
            },
            eventCategoryId: eventCategory.id,
          }
        }
      },
    },
    getAllEventCategories: {
      query: GET_ALL_EVENT_CATEGORIES,
      result({ data, loading }) {
        if (!loading && data) {
          this.categories = data.getAllEventCategories
        }
      },
    },
  },
}
</script>

<style scoped>
.table-layout {
  border-collapse: separate;
  width: 100%;
  border-spacing: 10px;
  display: table;
}

.table-cell-layout {
  display: table-cell;
}

.import-export-title {
  text-align: center;
}

.update-button {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

@media (max-width: 400px) {
  .update-button {
    justify-content: center;
  }
}

.v-btn {
  margin: 2rem 0 0 0 !important;
}

.category-section {
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
}

@media screen and (max-width: 800px) {
  .category-section {
    margin-top: 1.5rem;
  }
}
</style>
